

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

import {
  packSetlist,
  cacheAssets,
  uncacheAssets,
} from "./setlistsAPI";
import initialState from "./initialState";
import initialSessionState from "../session/initialState";

import {
  unpackSession,
  unpackSetlist,
} from "../session/sessionAPI";
import { removeAssetFromCache } from "../../services/assetStore";
import { triggerBlobDownload } from "../../factories/ui";
import { addNotificationAsync } from "../ui/uiSlice";
import {
  fetchUserSetlistsFromS3,
  uploadUserSetlistsToS3,
} from "../../utils/userSetlistsS3";


// pgw 30-sep-2025 - replace all debugging debugLog calls with debugLog
import { debugLog } from '../../debugLog';

// pgw 20-apr-2026 - testing CG integration
import {
  isCgTestingEnabled,
  doesCgAuthTokenExist,
  getCgAuthHeaders,
  normaliseChoirGeniusSetlist,
  fetchCgSetlistContents,
  getChoirGeniusSetlistsApiUrl,
  mapCgMusicItemToLibrarySong,
  getCgLyricsFileUrl,
  getCgChaptersFileUrl,
  fetchCgTextFile,
  parseCgChaptersText,
} from "../../utils/cgTesting";


// pgw 09-feb-2026
let doprint = false;
// let doprint = true;



export const cacheSetlistAssetsAsync = createAsyncThunk(
  "setlists/cacheSetlist",
  async (payload, thunkAPI) => {

// pgw 05-nov-2025
	if (doprint) console.log('cacheSetlistAssetsAsync payload.items.length = ', payload.items.length)
	if (doprint) console.log('cacheSetlistAssetsAsync payload.id = ', payload.id)

	debugLog('cacheSetlistAssetsAsync payload.items.length = ', payload.items.length)

    if (payload.items.length === 0) {
	if (doprint) console.log('Should not cache setlist because there are no songs in it ')
      // Should not cache setlist because there are no songs in it
    } else {
      const setlist = {
        ...thunkAPI
          .getState()
          .setlists.setlists.find((s) => s.id === payload.id),
      };

	if (doprint) console.log('cacheSetlistAssetsAsync setlist = ', setlist)
	if (doprint) console.log('cacheSetlistAssetsAsync setlist.setlists = ', setlist.setlists)

      const assetUrls = [];
      for (const setlistSong of setlist.items) {

	if (doprint) console.log('cacheSetlistAssetsAsync setlistSong = ', setlistSong)
	if (doprint) console.log('cacheSetlistAssetsAsync setlistSong.pathzip = ', setlistSong.pathzip)
	if (doprint) console.log('cacheSetlistAssetsAsync setlistSong.lyricsUrl = ', setlistSong.pathzip)

        assetUrls.push(setlistSong.pathzip);

        // Add lyrics asset if song has it
        if (setlistSong.lyricsUrl) assetUrls.push(setlistSong.lyricsUrl);
      }

      await cacheAssets(assetUrls, (perentage) => {
        thunkAPI.dispatch(setCacheProgress(perentage));
      });
      setlist.cached = true;
      return setlist;
    }
  }
);

export const uncacheSetlistAssetsAsync = createAsyncThunk(
  "setlists/uncacheSetlist",
  async (payload, thunkAPI) => {

	debugLog('uncacheSetlistAssetsAsync p1 ')

    const setlist = {
      ...thunkAPI.getState().setlists.setlists.find((s) => s.id === payload.id),
    };
    const setlists = thunkAPI.getState().setlists.setlists;

    const setlistsWhichContainUrl = (urlToCheck) => {
      return setlists.filter((setlist) =>
        setlist.items.find((song) => song.pathzip === urlToCheck)
      );
    };
    const assetUrls = [];
    for (const setlistSong of setlist.items) {
      if (setlistsWhichContainUrl(setlistSong.pathzip).length < 2) {
        assetUrls.push(setlistSong.pathzip);
        // Add lyrics asset if song has it
        if (setlistSong.lyricsUrl) assetUrls.push(setlistSong.lyricsUrl);
      } else {
        // Found track into another playlist
      }
    }

    await uncacheAssets(assetUrls, (perentage) => {
      thunkAPI.dispatch(setCacheProgress(perentage));
    });

    setlist.cached = false;
    return setlist;
  }
);

export const unpackSessionAsync = createAsyncThunk(
  "session/unpack",
  async (payload, thunkAPI) => {
	debugLog('unpackSessionAsync p1 ')
    const sessionObject = await unpackSession(payload);
    return sessionObject;
  }
);

export const unpackSetlistAsync = createAsyncThunk(
  "session/unpackSetlist",
  async (payload, thunkAPI) => {
	debugLog('unpackSetlistAsync p1 ')
    const setlistObject = await unpackSetlist(payload);
    const setlistSessions = [];

    for (const setlistKey of Object.getOwnPropertyNames(setlistObject)) {
      const sessionBlob = setlistObject[setlistKey];
      const sessionObject = await unpackSession(sessionBlob);
      setlistSessions.push(sessionObject);
    }

    return setlistSessions;
  }
);

export const packSetlistAsync = createAsyncThunk(
  "session/packSetlist",
  async (payload, thunkAPI) => {
	debugLog('packSetlistAsync p1 ')
    // Get setlist session from state
    // Filter out all containing userAudio stems
    const sessions = thunkAPI
      .getState()
      .setlist.sessions.filter((s) => s.userStems.length === 0);

    // Add all session zips into one master .setlist zip file
    const setListBlob = await packSetlist(sessions);

    // Prompt download in browser 👍
    const dateNow = new Date();
    const setlistFilename = `mySetlist-${dateNow
      .toLocaleDateString()
      .replaceAll("/", "")}_${dateNow
      .toLocaleTimeString()
      .replaceAll(":", "")}.setlist`;
    triggerBlobDownload(setListBlob, setlistFilename);

	// pgw 13-jul-2025 remove notifications, as they mess with setlists and can break player rendering
	/*
    thunkAPI.dispatch(
      addNotificationAsync({
        title: "Setlist saved",
        text: `${setlistFilename} was downloaded`,
        type: "positive",
      })
    );
	*/

    return true;
  }
);

export const removeSessionFromSetlistAsync = createAsyncThunk(
  "setlsit/remove",
  async (payload, thunkAPI) => {
	debugLog('removeSessionFromSetlistAsync p1 ')
    // Remove associated items from cache
    const session = thunkAPI
      .getState()
      .setlist.sessions.find((s) => s.id === payload);
    for (const userStem of session.userStems) {
      await removeAssetFromCache(userStem.id);
    }
    // Remove associated song zip
    await removeAssetFromCache(session.song.pathzip);

    return payload;
  }
);

// pgw 16-jul-2025
// chatgpt solution:
/*
import { v4 as uuidv4 } from 'uuid';
import { sleep } from '../../utils/sleep'; // or define inline
import { persistor } from '../../store';
import { addSetlist } from './setlistsSlice';


reducers: {
  addSetlist: (state, action) => {
    state.setlists.push(action.payload);
  },
}


export const createNewEmptySetlist = (title, subtitle, imageUrl) => async (dispatch) => {
  const newSetlist = {
    id: uuidv4().slice(0, 8),
    title,
    subTitle: subtitle,
    imageUrl,
    createdAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
    cached: false,
    items: [],
    readOnly: false,
  };

  dispatch(addSetlist(newSetlist));

  await sleep(100);
  await persistor.flush();
};
export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
dispatch(createNewEmptySetlist('My Setlist', 'Optional subtitle', '/some/image.png'));


explain these:  thunk action ,  Redux Thunk , createAsyncThunk 

In Redux, a thunk is a function that delays logic (like side effects) until it’s ready to be executed by the Redux store.
Formally: A thunk is a function that returns another function.
e.g.
const thunkAction = () => (dispatch, getState) => {
  // this runs when you dispatch it
  debugLog("Thunk executing...");
  dispatch({ type: 'SOME_ACTION' });
};




import { v4 as uuidv4 } from 'uuid';
import { sleep } from '../../utils/sleep'; // or define inline
import { persistor } from '../../store';
import { addSetlist } from './setlistsSlice';

export const createNewEmptySetlist = (title, subtitle, imageUrl) => async (dispatch) => {
  const newSetlist = {
    id: uuidv4().slice(0, 8),
    title,
    subTitle: subtitle,
    imageUrl,
    createdAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
    cached: false,
    items: [],
    readOnly: false,
  };

  dispatch(addSetlist(newSetlist));

  await sleep(100);
  await persistor.flush();
};


*/

/*

	// pgw 20-may-2025 testing
	// add x-auth-token to call
    const response = {
        data: await ky.get(url, {
      		headers: { 'x-auth-token': '1cd551a4d43f6088cce7c91f9a9b40b258155c92c0a1db0e289a4dd6da078c5b' },
            onDownloadProgress: progressHandler,
			timeout: 60000
        }).arrayBuffer()
    }

*/

// getCgAuthHeaders()
// pgw 20-apr-2026 - testing CG integration
export const fetchChoirGeniusSetlistsAsync = createAsyncThunk(
  "setlists/fetchChoirGeniusSetlists",
  async (_, thunkAPI) => {

	if (doprint) console.log('fetchChoirGeniusSetlistsAsync p1');

    if (!isCgTestingEnabled()) {
		return [];
    }

	if (doesCgAuthTokenExist() == false) {
	  return [];
	}


    const response = await fetch(getChoirGeniusSetlistsApiUrl(), {
      method: "GET",
      headers: getCgAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`ChoirGenius setlists request failed: ${response.status}`);
    }

    const json = await response.json();

	if (doprint) console.log('fetchChoirGeniusSetlistsAsync p1 json = ', json);

    const rawSetlists = Array.isArray(json) ? json : (json?.setlists || []);

    return rawSetlists.map(normaliseChoirGeniusSetlist);
  }
);


export const loadCgSetlistItemsAsync = createAsyncThunk(
  "setlists/loadCgSetlistItems",
  async (setlistId, thunkAPI) => {


	if (doprint) console.log('loadCgSetlistItemsAsync p1 isCgTestingEnabled() = ', isCgTestingEnabled())
	if (doprint) console.log('loadCgSetlistItemsAsync p1 doesCgAuthTokenExist() = ', doesCgAuthTokenExist())

    if (!isCgTestingEnabled()) return;

    const state = thunkAPI.getState();
    const setlist = state.setlists.setlists.find(s => s.id === setlistId);

	if (doprint) console.log('loadCgSetlistItemsAsync p2 state.setlists.setlists = ', state.setlists.setlists)
	if (doprint) console.log('loadCgSetlistItemsAsync p2 setlist = ', setlist)


    if (!setlist || !setlist.cgSetlistId) return;

	if (doprint) console.log('loadCgSetlistItemsAsync p2 setlist.cgSetlistId = ', setlist.cgSetlistId)


    // already loaded → skip
    if (setlist.items && setlist.items.length > 0) {
      return { setlistId, items: setlist.items };
    }


    // fetch CG items
    const rawItems = await fetchCgSetlistContents(setlist.cgSetlistId);


	if (doprint) console.log('loadCgSetlistItemsAsync p2 rawItems = ', rawItems)


    // get library (already loaded earlier)
    const librarySongs = state.library?.songs || [];

    const mappedItems = await Promise.all(
      rawItems.map(async (item) => {
        const lyricsUrl = getCgLyricsFileUrl(item);
        const chaptersUrl = getCgChaptersFileUrl(item);

        let chapters = [];


		if (doprint) console.log('loadCgSetlistItemsAsync p3 chaptersUrl = ', chaptersUrl)


        if (chaptersUrl) {
          try {
            const chaptersText = await fetchCgTextFile(chaptersUrl);

			if (doprint) console.log('loadCgSetlistItemsAsync p4 chaptersText = ', chaptersText)

            chapters = parseCgChaptersText(chaptersText);
			if (doprint) console.log('loadCgSetlistItemsAsync p5 chapters = ', chapters)
          } catch (e) {
            console.warn("Could not load CG chapters file", chaptersUrl, e);
          }
        }

        return mapCgMusicItemToLibrarySong(item, librarySongs, {
          lyricsUrl,
          chapters,
        });
      })
    );




    return {
      setlistId,
      items: mappedItems,
    };
  }
);


const setlistsFingerprint = (setlists) => {
  try {
    return JSON.stringify(Array.isArray(setlists) ? setlists : []);
  } catch (e) {
    return "";
  }
};

export const loadUserSetlistsFromS3Async = createAsyncThunk(
  "setlists/loadUserSetlistsFromS3",
  async (_, thunkAPI) => {

    if (window.localStorage.getItem("saveSetlists") !== "1") {
      if (doprint) console.log("[SETLISTS S3] not loading because saveSetlists is not 1");
      return null;
    }

    const user = thunkAPI.getState().auth.user;

    if (!user || user.guest) {
      if (doprint) console.log("[SETLISTS S3] not loading because user is not logged in");
      return null;
    }

    const email = user.userEmail || user.userName || window.localStorage.getItem("UGC_USER_EMAIL");

    if (!email) {
      if (doprint) console.log("[SETLISTS S3] not loading because email is missing");
      return null;
    }

    const setlists = await fetchUserSetlistsFromS3(email);

    return {
      email,
      setlists,
    };
  }
);


export const syncUserSetlistsWithS3Async = createAsyncThunk(
  "setlists/syncUserSetlistsWithS3",
  async (_, thunkAPI) => {
    if (window.localStorage.getItem("saveSetlists") !== "1") {
      if (doprint) console.log("[SETLISTS S3] not syncing because saveSetlists is not 1");
      return null;
    }

    const state = thunkAPI.getState();
    const user = state.auth.user;

    if (!user || user.guest) {
      if (doprint) console.log("[SETLISTS S3] not syncing because user is not logged in");
      return null;
    }

    const email = user.userEmail || user.userName || window.localStorage.getItem("UGC_USER_EMAIL");

    if (!email) {
      if (doprint) console.log("[SETLISTS S3] not syncing because email is missing");
      return null;
    }

    const localSetlists = state.setlists.setlists || [];
    const remoteSetlists = await fetchUserSetlistsFromS3(email);

    const localFingerprint = setlistsFingerprint(localSetlists);
    const remoteFingerprint = setlistsFingerprint(remoteSetlists);

	if (doprint) {
		console.log("[SETLISTS S3] sync compare", {
		  email,
		  localCount: localSetlists.length,
		  remoteCount: remoteSetlists.length,
		  localFingerprint,
		  remoteFingerprint,
		  same: localFingerprint === remoteFingerprint,
		});
	}

    if (remoteSetlists.length > 0 && localFingerprint !== remoteFingerprint) {
      return {
        email,
        action: "replace-local-from-remote",
        setlists: remoteSetlists,
      };
    }

    if (remoteSetlists.length === 0 && localSetlists.length > 0) {
      const publicUrl = await uploadUserSetlistsToS3(localSetlists, email);

      return {
        email,
        action: "uploaded-local-to-empty-remote",
        publicUrl,
        setlists: localSetlists,
      };
    }

    return {
      email,
      action: "already-in-sync",
      setlists: localSetlists,
    };
  }
);

export const saveUserSetlistsToS3Async = createAsyncThunk(
  "setlists/saveUserSetlistsToS3",

  async (_, thunkAPI) => {
    if (window.localStorage.getItem("saveSetlists") !== "1") {
      if (doprint) console.log("[SETLISTS S3] not saving because saveSetlists is not 1");
      return null;
    }

    const state = thunkAPI.getState();
    const user = state.auth.user;

    if (!user || user.guest) {
      if (doprint) console.log("[SETLISTS S3] not saving because user is not logged in");
      return null;
    }

    const email = user.userEmail || user.userName || window.localStorage.getItem("UGC_USER_EMAIL");

    if (!email) {
      if (doprint) console.log("[SETLISTS S3] not saving because email is missing");
      return null;
    }

    const setlists = state.setlists.setlists || [];

    const publicUrl = await uploadUserSetlistsToS3(setlists, email);

    return {
      email,
      publicUrl,
      count: setlists.length,
    };
  }
);

export const setlistsSlice = createSlice({
  name: "setlists",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    createNewEmptySetlist: (state, action) => {
		if (doprint) console.log('createNewEmptySetlist p1 ')

      // pgw 20-apr-2026 - testing CG integration
      if (isCgTestingEnabled()) {
        alert("createNewEmptySetlist blocked because cgTesting is enabled");
        return;
      }

      const newSetlist = {
        id: uuidv4().slice(0, 8),
        title: action.payload.title,
        subTitle: action.payload.subtitle,
        imageUrl: action.payload.imageUrl,
        createdAt: new Date().toISOString(),
        lastUpdatedAt: new Date().toISOString(),
        cached: false,
        items: [],
        readOnly: false
      };
      state.setlists.push(newSetlist);
    },
    createSetlistFromData: (state, action) => {
		debugLog('createSetlistFromData p1 ')

	  if (doprint) console.log("[SETLISTS SLICE] createSetlistFromData state = ", state);
	  if (doprint) console.log("[SETLISTS SLICE] createSetlistFromData action = ", action);


      // pgw 20-apr-2026 - testing CG integration
      if (isCgTestingEnabled()) {
        alert("createSetlistFromData blocked because cgTesting is enabled");
        return;
      }

      const newSetlist = {
        id: action.payload.setlistId || uuidv4().slice(0, 8),
        title: action.payload.title,
        subTitle: action.payload.subtitle,
        imageUrl: action.payload.imageUrl,
        createdAt: new Date().toISOString(),
        lastUpdatedAt: new Date().toISOString(),
        cached: false,
        items: action.payload.items,
        readOnly: action.payload.readOnly
      };
	  if (doprint) console.log("[SETLISTS SLICE] createSetlistFromData action.payload = ", action.payload);
	  if (doprint) console.log("[SETLISTS SLICE] createSetlistFromData newSetlist = ", newSetlist);
      state.setlists.push(newSetlist);
    },
    removeSetListFromSetlists: (state, action) => {
		debugLog('removeSetListFromSetlists p1 ')
      state.setlists = state.setlists.filter(
        (setlist) => setlist.id !== action.payload
      );
    },
    setCacheProgress: (state, action) => {
      state.cachingProgress = action.payload;
    },

    addSongToSetlist: (state, action) => {

		// pgw 24-sep-2025
		debugLog('addSongToSetlist state = ', state)
		debugLog('addSongToSetlist action = ', action)
		debugLog('addSongToSetlist state.setlists = ', state.setlists)
		debugLog('addSongToSetlist action.payload = ', action.payload)


      const setlist = state.setlists.find(
        (setlist) => setlist.id === action.payload.setlistId
      );
      if (setlist) {

        // pgw 20-apr-2026 - testing CG integration
        if (setlist.readOnly) {
          debugLog("addSongToSetlist blocked: readOnly setlist", setlist.id);
          return;
        }

		debugLog('addSongToSetlist setlist = ', setlist)
		debugLog('addSongToSetlist setlist.items = ', setlist.items)
		debugLog('addSongToSetlist setlist.title = ', setlist.title)
		debugLog('addSongToSetlist setlist.cached = ', setlist.cached)
		debugLog('addSongToSetlist setlist.readOnly = ', setlist.readOnly)

		if (setlist.cached) {
			debugLog('addSongToSetlist setlist.cached => alert user that song will not be cached')
// 
// 
// 			let res = confirm("That setlist is set to be available offline.\nDo you want to continue (OK to add anyway; Cancel to do nothing) ?")
// 			debugLog('res = ', res);
// 

			let confStr = "That setlist is set to be available offline."
			confStr = confStr + "\n" + "However, that is only true for songs that are already in that setlist."
			confStr = confStr + "\n" + "If you add this song, it will NOT be available offline."
			confStr = confStr + "\n" + "To make all songs in that setlist available offline"
			confStr = confStr + "\n" + "you need to do the following:"
			confStr = confStr + "\n" + '. change the setlist so that "Available offline" is off,'
			confStr = confStr + "\n" + '. add this song to that setlist,'
			confStr = confStr + "\n" + '. change the setlist so that "Available offline" is on.'
			confStr = confStr + "\n" + "\n" + "Are you sure you want to continue ?"
			confStr = confStr + "\n" + "(OK to add this song to the setlist; Cancel to do nothing)"
			let res = confirm(confStr)
			if (res) {
				debugLog("You pressed OK!");
			} else {
				debugLog("You pressed Cancel - returning...");
				return;
			}



		}
		else {
			debugLog('addSongToSetlist setlist.cached false => no need to alert user')
		}


        if (
          !setlist.items.find(
            (song) => song.songId === action.payload.song.songId
          )
        ) {
          setlist.items = [...setlist.items, action.payload.song];
        }
      }
      state.setlists = [...state.setlists];
    },
    removeSongFromSetlist: (state, action) => {

          if (doprint) console.log("removeSongFromSetlist ");


      const setlist = state.setlists.find(
        (setlist) => setlist.id === action.payload.setlistId
      );
      if (setlist) {

        // pgw 20-apr-2026 - testing CG integration
        if (setlist.readOnly) {
          if (doprint) console.log("removeSongFromSetlist blocked: readOnly setlist", setlist.id);
          return;
        }


        setlist.items = [
          ...setlist.items.filter(
            (song) => song.songId !== action.payload.song.songId
          ),
        ];
      }
      state.setlists = [...state.setlists];
    },


	// features/setlists/setlistsSlice.js

	updateSetlistItemMetadata: (state, action) => {
	  const { setlistId, itemIx, songPatch } = action.payload || {};

	  const setlist = (state.setlists || []).find(
		(s) => String(s.id) === String(setlistId) || String(s.cgSetlistId) === String(setlistId)
	  );

	  if (!setlist || typeof itemIx !== "number" || !setlist.items?.[itemIx]) {
		return;
	  }

	  setlist.items[itemIx] = {
		...setlist.items[itemIx],
		 ...songPatch,
	  };

	  state.setlists = [...state.setlists];
	},

	updateSetlistItemSettings: (state, action) => {
	  const { setlistId, itemIx, itemSettings } = action.payload || {};
	  if (!setlistId && setlistId !== "") return;
	  if (typeof itemIx !== "number") return;

	  // This slice stores setlists in state.setlists (array), not byId.
	  const setlist = (state.setlists || []).find(s => s.id === setlistId);
	  const item = setlist?.items?.[itemIx];
	  if (!item) {
		if (doprint) console.log("[SETLISTS SLICE] updateSetlistItemSettings target item missing", {
		  setlistId,
		  itemIx,
		  hasSetlist: !!setlist,
		  itemsLen: setlist?.items?.length,
		});
		return;
	  }

	  // MUST match what Setlist open reads.
	  item.itemSettings = itemSettings || null;

	  // Force immutability at the top level so selectors rerender.
	  state.setlists = [...state.setlists];

	  if (doprint) console.log("[SETLISTS SLICE] updateSetlistItemSettings applied", {
		setlistId,
		itemIx,
		stemCount: itemSettings?.stems?.length,
	  });
	},


	// pgw 08-feb-2026
	renameSetlist: (state, action) => {
	  const { setlistId, title, subTitle } = action.payload || {};
	  if (!setlistId) return;

	  // pgw 20-apr-2026 - testing CG integration
	  if (setlist.readOnly) {
		alert("renameSetlist blocked: readOnly setlist", setlist.id);
		return;
	  }


	  const setlist = (state.setlists || []).find(s => s.id === setlistId);
	  if (!setlist) return;

	  if (typeof title === "string") setlist.title = title;
	  if (typeof subTitle === "string") setlist.subTitle = subTitle;

	  setlist.lastUpdatedAt = new Date().toISOString();

	  // keep your existing “force immutability” pattern
	  state.setlists = [...state.setlists];

	  if (doprint) console.log("[SETLISTS SLICE] renameSetlist applied", {
		setlistId,
		title: setlist.title,
		subTitle: setlist.subTitle,
	  });
	},


	// pgw 20-apr-2026 - testing CG integration
    replaceAllSetlists: (state, action) => {
          if (doprint) console.log("replaceAllSetlists ");

	  if (doprint) console.log('reducers replaceAllSetlists p1 state.setlists = ', state.setlists);

      state.setlists = Array.isArray(action.payload) ? action.payload : [];

	  if (doprint) console.log('reducers replaceAllSetlists p1 state.setlists = ', state.setlists);
		state.setlists = [];
	  if (doprint) console.log('reducers replaceAllSetlists p1 state.setlists = ', state.setlists);

    },


  },
  extraReducers: (builder) => {
    builder


	  // pgw 20-apr-2026 - testing CG integration
      .addCase(loadUserSetlistsFromS3Async.pending, (state) => {
        if (doprint) console.log("[SETLISTS S3] load pending");
        state.status = "loading";
      })
      .addCase(loadUserSetlistsFromS3Async.rejected, (state, action) => {
        console.error("[SETLISTS S3] load failed", action.error);
        state.status = "error";
      })
      .addCase(loadUserSetlistsFromS3Async.fulfilled, (state, action) => {
        if (!action.payload) {
          state.status = "idle";
          return;
        }

        const remoteSetlists = action.payload.setlists || [];

        if (doprint) console.log("[SETLISTS S3] load fulfilled", {
          count: remoteSetlists.length,
          titles: remoteSetlists.map((setlist) => setlist.title),
        });

        if (remoteSetlists.length > 0) {
          state.setlists = remoteSetlists;
        }

        state.status = "idle";
      })


      .addCase(syncUserSetlistsWithS3Async.fulfilled, (state, action) => {
        if (!action.payload) {
          return;
        }

        if (doprint) console.log("[SETLISTS S3] sync fulfilled", action.payload);

        if (action.payload.action === "replace-local-from-remote") {
          state.setlists = action.payload.setlists || [];
        }

        state.status = "idle";
      })
      .addCase(saveUserSetlistsToS3Async.fulfilled, (state, action) => {
        if (action.payload) {
          if (doprint) console.log("[SETLISTS S3] save fulfilled", action.payload);
        }
      })

	  // pgw 20-apr-2026 - testing CG integration
      .addCase(fetchChoirGeniusSetlistsAsync.pending, (state) => {


			if (doprint) console.log('extraReducers fetchChoirGeniusSetlistsAsync.pending p1');

        state.status = "loading";
      })
      .addCase(fetchChoirGeniusSetlistsAsync.rejected, (state, action) => {
			if (doprint) console.log('extraReducers fetchChoirGeniusSetlistsAsync.rejected p1');
        state.status = "error";
        console.error(action);
      })
      .addCase(fetchChoirGeniusSetlistsAsync.fulfilled, (state, action) => {
			if (doprint) console.log('extraReducers fetchChoirGeniusSetlistsAsync.fulfilled p1');
        state.setlists = action.payload || [];
        state.status = "idle";
      })


	  .addCase(loadCgSetlistItemsAsync.fulfilled, (state, action) => {

			if (doprint) console.log('extraReducers loadCgSetlistItemsAsync.fulfilled p1');

		const { setlistId, items } = action.payload || {};

		const setlist = state.setlists.find(s => s.id === setlistId);
		if (!setlist) return;

		setlist.items = items || [];
	  })

      .addCase(removeSessionFromSetlistAsync.fulfilled, (state, action) => {
        state.sessions = state.sessions.filter((s) => s.id !== action.payload);
      })
      .addCase(packSetlistAsync.pending, (state, action) => {})
      .addCase(packSetlistAsync.rejected, (state, action) => {
        state.status = "error";
        console.error(action);
      })
      .addCase(packSetlistAsync.fulfilled, (state, action) => {})
      .addCase(cacheSetlistAssetsAsync.pending, (state, action) => {
        state.status = "caching";
        state.cachingProgress = 0.01;
      })
      .addCase(cacheSetlistAssetsAsync.rejected, (state, action) => {
        state.status = "error";
        console.error(action);
        state.cachingProgress = null;
      })
      .addCase(cacheSetlistAssetsAsync.fulfilled, (state, action) => {
        let existingSetlists = [...state.setlists];
        existingSetlists.find(
          (setlist) => setlist.id === action.payload.id
        ).cached = true;
        state.cachingProgress = null;
        state.setlists = existingSetlists;
        state.status = "idle";
      })
      .addCase(uncacheSetlistAssetsAsync.pending, (state, action) => {
        state.status = "uncaching";
      })
      .addCase(uncacheSetlistAssetsAsync.rejected, (state, action) => {
        state.status = "error";
        state.cachingProgress = null;
      })
      .addCase(uncacheSetlistAssetsAsync.fulfilled, (state, action) => {
        let existingSetlists = [...state.setlists];
        existingSetlists.find(
          (setlist) => setlist.id === action.payload.id
        ).cached = false;
        state.cachingProgress = null;
        state.setlists = existingSetlists;
        state.status = "idle";
      })

      .addCase(unpackSessionAsync.pending, (state, action) => {
        state.status = "unpacking";
        state.loadingProgress = 7;
      })
      .addCase(unpackSessionAsync.rejected, (state, action) => {
        state.status = "error";
        console.error(action);
      })
      .addCase(unpackSessionAsync.fulfilled, (state, action) => {
        Object.getOwnPropertyNames(action.payload).forEach((propertyName) => {
          state[propertyName] = action.payload[propertyName];
        });
        const newSession = {
          ...action.payload,
          id: uuidv4().slice(0, 8),
          imported: true,
          importedAt: new Date().toISOString(),
        };
        state.sessions.push(newSession);
        state.status = "ready";
        state.loadingProgress = 10;
      })
      .addCase(unpackSetlistAsync.fulfilled, (state, action) => {
        const sessionPack = [];

        for (const session of action.payload) {
          const importedSession = {};
          Object.getOwnPropertyNames(session).forEach((propertyName) => {
            importedSession[propertyName] = session[propertyName];
          });
          const newSession = {
            ...importedSession,
            id: uuidv4().slice(0, 8),
            imported: true,
            importedAt: new Date().toISOString(),
          };
          sessionPack.push(newSession);
        }
        state.sessions = [...state.sessions, ...sessionPack];
      });
  },
});


// pgw 20-apr-2026 - testing CG integration

/*
export const {
  createNewEmptySetlist,
  createSetlistFromData,
  removeSetListFromSetlists,
  addSongToSetlist,
  removeSongFromSetlist,
  setCacheProgress,
  updateSetlistItemSettings,
  renameSetlist,
} = setlistsSlice.actions;
*/

export const {
  createNewEmptySetlist,
  createSetlistFromData,
  removeSetListFromSetlists,
  addSongToSetlist,
  removeSongFromSetlist,
  setCacheProgress,

  updateSetlistItemSettings,
  updateSetlistItemMetadata,

  renameSetlist,
  replaceAllSetlists,
} = setlistsSlice.actions;


export const selectSetlistsSetlists = (state) => state.setlists.setlists;
export const selectSetlistsCachingProgress = (state) =>
  state.setlists.cachingProgress;
export const selectSetlistsStatus = (state) => state.setlists.status;
export default setlistsSlice.reducer;
